import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import toast from "react-hot-toast";
import { fetchSwapHistory } from "../services/swapHistoryApi";
import { FaSearch, FaSyncAlt, FaChevronLeft, FaChevronRight, FaInfoCircle, FaHistory, FaRandom } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";

const PAGE_SIZE = 20;

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const dt = new Date(v);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

function normalizeSwapType(type) {
    const t = String(type || "").toLowerCase().replace(/_/g, " ").trim();
    if (t.includes("usdt") && t.includes("coin")) {
        if (t.indexOf("usdt") < t.indexOf("coin")) return "usdt-to-coin";
        return "coin-to-usdt";
    }
    if (t === "buy") return "usdt-to-coin";
    if (t === "sell") return "coin-to-usdt";
    return "unknown";
}

function swapTypeMeta(type) {
    const kind = normalizeSwapType(type);
    if (kind === "usdt-to-coin") {
        return { kind, label: "USDT → Coin", className: "swap-type-usdt-coin" };
    }
    if (kind === "coin-to-usdt") {
        return { kind, label: "Coin → USDT", className: "swap-type-coin-usdt" };
    }
    return { kind: "unknown", label: safeText(type), className: "swap-type-unknown" };
}

function TypeBadge({ type }) {
    const meta = swapTypeMeta(type);
    return <span className={`swap-type-badge ${meta.className}`}>{meta.label}</span>;
}

function LoadingRow({ cols = 7 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span className="text-muted small">
                    Page {pageNo} of {totalPages}
                </span>
            </div>

            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>

                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: "auto" }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

function SwapDetailsModal({ open, item, onClose }) {
    if (!open || !item) return null;

    const u = item?.user || {};

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1040,
                        }}
                    />

                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                            overflowY: "auto",
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "720px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                <div
                                    className={`modal-header border-bottom-0 p-3 swap-modal-header ${swapTypeMeta(item?.type).className}`}
                                >
                                    <div className="d-flex align-items-center w-100 text-white">
                                        <div className="flex-grow-1">
                                            <h5 className="modal-title fw-bold mb-0" style={{ fontSize: "1.1rem" }}>
                                                <FaHistory className="me-2" />
                                                Swap Details
                                            </h5>
                                        </div>
                                        <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
                                    </div>
                                </div>

                                <div className="modal-body p-3">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Type</div>
                                                            <div className="mt-1">
                                                                <TypeBadge type={item?.type} />
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Date</div>
                                                            <div className="fw-semibold mt-1">{formatDateTime(item?.create_date)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Coin</div>
                                                            <div className="fw-bold mt-1">{nfmt(item?.coin, 2)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <p className="text-muted small mb-0">USDT</p>
                                                            <div className="fw-bold mt-1">{nfmt(item?.usdt, 2)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <h6 className="fw-bold text-primary mb-3" style={{ fontSize: "0.95rem" }}>
                                                        User
                                                    </h6>
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Name</div>
                                                            <div className="fw-semibold mt-1">{safeText(u?.name)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Mobile</div>
                                                            <div className="fw-semibold mt-1">
                                                                {safeText(u?.country_code)} {safeText(u?.mobile)}
                                                            </div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Email</div>
                                                            <div className="fw-semibold mt-1" style={{ wordBreak: "break-word" }}>
                                                                {safeText(u?.email)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer border-top-0 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <button className="btn btn-secondary px-3" type="button" onClick={onClose} style={{ fontSize: "0.85rem" }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default function SwapHistory() {
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [meta, setMeta] = useState({ total: 0, limit: PAGE_SIZE, page: 1, total_pages: 1 });
    const [selectedItem, setSelectedItem] = useState(null);

    const totalPages = useMemo(() => {
        const fromMeta = Number(meta?.total_pages || 0);
        if (fromMeta > 0) return fromMeta;
        const total = Number(meta?.total || 0);
        const limit = Number(meta?.limit || PAGE_SIZE);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [meta?.total_pages, meta?.total, meta?.limit]);

    const fetchHistory = useCallback(async ({ page = 1 } = {}) => {
        setLoading(true);
        try {
            const out = await fetchSwapHistory({
                token: localStorage.getItem("token") || "",
                page_no: page,
                limit: PAGE_SIZE,
            });

            if (out?.error) {
                toast.error(typeof out?.error === "string" ? out.error : "Failed to load Swap history");
                setList([]);
                setMeta({ total: 0, limit: PAGE_SIZE, page, total_pages: 1 });
                return;
            }

            const rows = Array.isArray(out?.data) ? out.data : [];
            const apiMeta = out?.meta || {};

            setList(rows);
            setMeta({
                total: Number(apiMeta?.total || 0),
                limit: Number(apiMeta?.limit || PAGE_SIZE),
                page: Number(apiMeta?.page || page),
                total_pages: Number(apiMeta?.total_pages || 1),
            });
        } catch (err) {
            console.log(err);
            toast.error("Failed to load Swap history");
            setList([]);
            setMeta({ total: 0, limit: PAGE_SIZE, page, total_pages: 1 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHistory({ page: pageNo });
    }, [pageNo, fetchHistory]);

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));
    const onRefresh = () => fetchHistory({ page: pageNo });

    return (
        <PageTransition>
            <div className="container-fluid py-3 swap-history-page">
                <div className="swap-hero mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h1 className="h3 mb-2 d-flex align-items-center gap-2 fw-bold">
                                <FaRandom />
                                Swap History
                            </h1>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="swap-stat-pill">
                                    Total records: <strong>{nfmt(meta.total, 0)}</strong>
                                </span>
                                <span className="swap-type-badge swap-type-usdt-coin">USDT → Coin</span>
                                <span className="swap-type-badge swap-type-coin-usdt">Coin → USDT</span>
                            </div>
                        </div>
                        <button className="btn btn-light swap-refresh-btn" onClick={onRefresh} disabled={loading}>
                            <FaSyncAlt className={`me-2 ${loading ? "spin" : ""}`} />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card swap-table-card border-0 shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 swap-history-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "8%" }}>S.No</th>
                                                <th style={{ width: "22%" }}>Date</th>
                                                <th style={{ width: "10%" }}>Type</th>
                                                <th style={{ width: "14%" }}>Coin</th>
                                                <th style={{ width: "14%" }}>USDT</th>
                                                <th style={{ width: "26%" }}>User</th>
                                                <th style={{ width: "6%" }} className="text-center">
                                                    <FaInfoCircle size={12} title="Info" />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[...Array(5)].map((_, i) => (
                                                        <LoadingRow key={i} cols={7} />
                                                    ))}
                                                </>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No Swap history found</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((row, idx) => (
                                                    <tr key={`${row?.swap_id || ""}_${idx}`}>
                                                        <td className="fw-semibold">
                                                            {(pageNo - 1) * Number(meta?.limit || PAGE_SIZE) + idx + 1}
                                                        </td>
                                                        <td>{formatDateTime(row?.create_date)}</td>
                                                        <td>
                                                            <TypeBadge type={row?.type} />
                                                        </td>
                                                        <td className="fw-semibold swap-amount-coin">{nfmt(row?.coin, 2)}</td>
                                                        <td className="fw-semibold swap-amount-usdt">{nfmt(row?.usdt, 2)}</td>
                                                        <td>
                                                            <div className="fw-semibold">{safeText(row?.user?.name)}</div>
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-info px-1 py-0 swap-info-btn"
                                                                onClick={() => setSelectedItem(row)}
                                                                title="View details"
                                                            >
                                                                <FaInfoCircle size={12} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <SwapDetailsModal open={!!selectedItem} item={selectedItem} onClose={() => setSelectedItem(null)} />

                <style jsx>{`
                    .swap-history-page {
                        --swap-green: #198754;
                        --swap-green-soft: rgba(25, 135, 84, 0.12);
                        --swap-red: #dc3545;
                        --swap-red-soft: rgba(220, 53, 69, 0.12);
                    }
                    .swap-hero {
                        border-radius: 16px;
                        padding: 1.25rem 1.5rem;
                        background: linear-gradient(135deg, rgba(25, 135, 84, 0.14), rgba(13, 110, 253, 0.1));
                        border: 1px solid rgba(25, 135, 84, 0.18);
                    }
                    .swap-stat-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.35rem;
                        padding: 0.35rem 0.75rem;
                        border-radius: 999px;
                        background: rgba(255, 255, 255, 0.85);
                        font-size: 0.875rem;
                    }
                    .swap-refresh-btn {
                        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
                    }
                    .swap-table-card {
                        border-radius: 14px;
                        overflow: hidden;
                    }
                    .swap-history-table thead {
                        background: linear-gradient(90deg, #157347, #0d6efd);
                        color: #fff;
                    }
                    .swap-history-table thead th {
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        border: none;
                        padding: 12px;
                    }
                    .swap-history-table tbody tr:hover {
                        background: rgba(13, 110, 253, 0.04);
                    }
                    .swap-history-table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                    .swap-type-badge {
                        display: inline-flex;
                        align-items: center;
                        padding: 0.35rem 0.65rem;
                        border-radius: 999px;
                        font-size: 0.75rem;
                        font-weight: 700;
                        letter-spacing: 0.02em;
                        white-space: nowrap;
                    }
                    .swap-type-usdt-coin {
                        color: #146c43;
                        background: var(--swap-green-soft);
                        border: 1px solid rgba(25, 135, 84, 0.35);
                    }
                    .swap-type-coin-usdt {
                        color: #b02a37;
                        background: var(--swap-red-soft);
                        border: 1px solid rgba(220, 53, 69, 0.35);
                    }
                    .swap-type-unknown {
                        color: #495057;
                        background: rgba(108, 117, 125, 0.12);
                        border: 1px solid rgba(108, 117, 125, 0.25);
                    }
                    .swap-modal-header.swap-type-usdt-coin {
                        background: linear-gradient(135deg, #198754, #20c997);
                        color: #fff;
                    }
                    .swap-modal-header.swap-type-coin-usdt {
                        background: linear-gradient(135deg, #dc3545, #fd7e14);
                        color: #fff;
                    }
                    .swap-modal-header.swap-type-unknown {
                        background: linear-gradient(135deg, #6c757d, #adb5bd);
                        color: #fff;
                    }
                    .swap-amount-coin {
                        color: #0d6efd;
                    }
                    .swap-amount-usdt {
                        color: #198754;
                    }
                    .swap-info-btn:hover {
                        background: #0dcaf0;
                        border-color: #0dcaf0;
                        color: #fff;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>
            </div>
        </PageTransition>
    );
}
